import { useEffect, useRef, useState, type FormEvent } from 'react';
import { createPortal } from 'react-dom';
import { getIndustriesByGroup, OTHER_INDUSTRY_ID } from '../lib/industries';

interface CreateOrgModalProps {
    open: boolean;
    onClose: () => void;
    onSubmit: (payload: {
        name: string;
        industry: string;
        industryCustom?: string;
    }) => Promise<void>;
}

export function CreateOrgModal({ open, onClose, onSubmit }: CreateOrgModalProps) {
    const [name, setName] = useState('');
    const [industry, setIndustry] = useState<string>('construction');
    const [industryCustom, setIndustryCustom] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState(false);
    const dialogRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        if (open) {
            setName('');
            setIndustry('construction');
            setIndustryCustom('');
            setError(null);
            setSubmitting(false);
        }
    }, [open]);

    useEffect(() => {
        if (!open) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [open, onClose]);

    if (!open || typeof document === 'undefined') return null;

    const isOther = industry === OTHER_INDUSTRY_ID;
    const customMissing = isOther && !industryCustom.trim();

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();
        if (!name.trim() || customMissing) return;
        setError(null);
        setSubmitting(true);
        try {
            await onSubmit({
                name: name.trim(),
                industry,
                industryCustom: isOther ? industryCustom : undefined,
            });
            onClose();
        } catch (err) {
            setError((err as Error).message || 'Impossible de créer l\'organisation.');
            setSubmitting(false);
        }
    };

    return createPortal(
        <div
            className="modal-backdrop"
            onClick={(e) => {
                if (e.target === e.currentTarget) onClose();
            }}
            role="dialog"
            aria-modal="true"
            aria-labelledby="co-title"
        >
            <div className="modal modal--wide" ref={dialogRef}>
                <header className="modal-head">
                    <h2 id="co-title">Nouvelle organisation</h2>
                    <button
                        type="button"
                        className="modal-close"
                        onClick={onClose}
                        aria-label="Fermer"
                    >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="18" y1="6" x2="6" y2="18" />
                            <line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                    </button>
                </header>

                <form className="modal-form" onSubmit={handleSubmit}>
                    <p className="modal-intro">
                        Créez un nouvel espace pour une autre entreprise ou activité.
                        Vous pourrez y gérer ses propres générateurs et demandes.
                    </p>

                    <label className="field">
                        <span className="field-label">Nom de l'organisation</span>
                        <input
                            type="text"
                            required
                            autoFocus
                            maxLength={80}
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="DB-BAT, Climatisation Dupont, etc."
                        />
                    </label>

                    <div className="field">
                        <span className="field-label">Secteur d'activité</span>
                        <div className="industry-groups is-compact" role="radiogroup">
                            {getIndustriesByGroup().map(({ group, items }) => (
                                <div key={group.id} className="industry-group">
                                    <div className="industry-group-title">{group.label}</div>
                                    <div className="industry-grid is-compact">
                                        {items.map((i) => (
                                            <label
                                                key={i.id}
                                                className={`industry-chip is-textual${industry === i.id ? ' is-active' : ''}`}
                                            >
                                                <input
                                                    type="radio"
                                                    name="org-industry"
                                                    value={i.id}
                                                    checked={industry === i.id}
                                                    onChange={() => setIndustry(i.id)}
                                                />
                                                <span className="industry-label">{i.label}</span>
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>

                        {isOther && (
                            <label className="industry-custom">
                                <span className="field-label">Précisez le secteur</span>
                                <input
                                    type="text"
                                    required
                                    maxLength={60}
                                    value={industryCustom}
                                    onChange={(e) => setIndustryCustom(e.target.value)}
                                    placeholder="Agence immobilière, Serrurerie, Coursier…"
                                />
                            </label>
                        )}
                    </div>

                    {error && <div className="auth-error" role="alert">{error}</div>}

                    <div className="modal-actions">
                        <button type="button" className="btn-ghost" onClick={onClose} disabled={submitting}>
                            Annuler
                        </button>
                        <button
                            type="submit"
                            className="btn-primary"
                            disabled={submitting || !name.trim() || customMissing}
                        >
                            {submitting ? 'Création…' : 'Créer l\'organisation'}
                        </button>
                    </div>
                </form>
            </div>
        </div>,
        document.body
    );
}
